import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BookOpenIcon,
  BookmarkIcon,
  PencilSquareIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  LinkIcon,
  PlayIcon,
  LightBulbIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon, StarIcon } from '@heroicons/react/24/solid';
import api from '../../config/api';
import { toast } from 'react-hot-toast';

interface KnowledgePoint {
  id: string;
  name: string;
  subject: string;
  content?: string;
  summary?: string;
  keyPoints?: string[];
  formulas?: string[];
  examples?: { title: string; content: string; solution?: string }[];
  studyTips?: string;
  resources?: { type: string; title: string; url: string }[];
  relatedQuestions?: any[];
  viewCount?: number;
  averageMastery?: number;
}

interface RelatedPoint {
  id: string;
  name: string;
}

const KnowledgeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [knowledgePoint, setKnowledgePoint] = useState<KnowledgePoint | null>(null);
  const [relatedPoints, setRelatedPoints] = useState<RelatedPoint[]>([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [activeTab, setActiveTab] = useState<'content' | 'questions' | 'notes'>('content');

  // 加载知识点详情
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const [pointRes, relatedRes] = await Promise.all([
          api.get(`/knowledge/point/${id}`),
          api.get(`/knowledge/point/${id}/related`)
        ]);

        setKnowledgePoint(pointRes.data?.data || pointRes.data);
        setRelatedPoints(relatedRes.data?.data || relatedRes.data);
      } catch (error) {
        console.error('加载知识点失败:', error);
        toast.error('加载失败');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // 添加收藏
  const toggleBookmark = async () => {
    try {
      if (isBookmarked) {
        // 取消收藏逻辑
        toast.success('已取消收藏');
      } else {
        await api.post('/knowledge/notes', {
          knowledgePointId: id,
          type: 'bookmark',
          content: `收藏知识点：${knowledgePoint?.name}`
        });
        toast.success('已添加到收藏');
      }
      setIsBookmarked(!isBookmarked);
    } catch (error) {
      toast.error('操作失败');
    }
  };

  // 添加笔记
  const addNote = async () => {
    if (!noteContent.trim()) {
      toast.error('请输入笔记内容');
      return;
    }

    try {
      await api.post('/knowledge/notes', {
        knowledgePointId: id,
        type: 'note',
        content: noteContent
      });
      toast.success('笔记添加成功');
      setShowNoteModal(false);
      setNoteContent('');
    } catch (error) {
      toast.error('添加失败');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  if (!knowledgePoint) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpenIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">知识点不存在</h2>
          <Link
            to="/student/knowledge"
            className="text-blue-600 hover:text-blue-700"
          >
            返回知识库
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航 */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/student/knowledge"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-1" />
              返回知识库
            </Link>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleBookmark}
                className={`p-2 rounded-lg transition-colors ${
                  isBookmarked
                    ? 'bg-yellow-50 text-yellow-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {isBookmarked ? (
                  <BookmarkSolidIcon className="w-5 h-5" />
                ) : (
                  <BookmarkIcon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => setShowNoteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
                添加笔记
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* 知识点标题 */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0">
              <BookOpenIcon className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {knowledgePoint.name}
              </h1>
              {knowledgePoint.summary && (
                <p className="text-gray-600">{knowledgePoint.summary}</p>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <AcademicCapIcon className="w-4 h-4" />
                  {knowledgePoint.relatedQuestions?.length || 0} 道相关题目
                </span>
                <span>
                  浏览 {knowledgePoint.viewCount || 0} 次
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 标签页 */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          <div className="flex border-b">
            {[
              { id: 'content', label: '知识内容' },
              { id: 'questions', label: '相关题目' },
              { id: 'notes', label: '我的笔记' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'content' && (
              <div className="space-y-6">
                {/* 详细内容 */}
                {knowledgePoint.content && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                      详细内容
                    </h3>
                    <div className="prose prose-blue max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {knowledgePoint.content}
                      </p>
                    </div>
                  </div>
                )}

                {/* 重点内容 */}
                {knowledgePoint.keyPoints && knowledgePoint.keyPoints.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <LightBulbIcon className="w-5 h-5 text-yellow-500" />
                      重点内容
                    </h3>
                    <ul className="space-y-2">
                      {knowledgePoint.keyPoints.map((point, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg"
                        >
                          <CheckCircleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 公式 */}
                {knowledgePoint.formulas && knowledgePoint.formulas.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      相关公式
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      {knowledgePoint.formulas.map((formula, index) => (
                        <div
                          key={index}
                          className="font-mono text-gray-800 p-2 bg-white rounded border"
                        >
                          {formula}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 示例 */}
                {knowledgePoint.examples && knowledgePoint.examples.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      示例
                    </h3>
                    <div className="space-y-4">
                      {knowledgePoint.examples.map((example, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-xl overflow-hidden"
                        >
                          <div className="bg-gray-50 px-4 py-2 font-medium text-gray-900">
                            {example.title}
                          </div>
                          <div className="p-4">
                            <p className="text-gray-700 mb-3">{example.content}</p>
                            {example.solution && (
                              <div className="bg-green-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-green-700">解答：</span>
                                <p className="text-gray-700 mt-1">{example.solution}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 学习建议 */}
                {knowledgePoint.studyTips && (
                  <div className="bg-blue-50 rounded-xl p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">学习建议</h3>
                    <p className="text-blue-800">{knowledgePoint.studyTips}</p>
                  </div>
                )}

                {/* 学习资源 */}
                {knowledgePoint.resources && knowledgePoint.resources.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <LinkIcon className="w-5 h-5 text-purple-500" />
                      学习资源
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {knowledgePoint.resources.map((resource, index) => (
                        <a
                          key={index}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                        >
                          {resource.type === 'video' && (
                            <PlayIcon className="w-5 h-5 text-red-500" />
                          )}
                          {resource.type === 'article' && (
                            <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                          )}
                          <span className="text-gray-700">{resource.title}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'questions' && (
              <div>
                {knowledgePoint.relatedQuestions && knowledgePoint.relatedQuestions.length > 0 ? (
                  <div className="space-y-3">
                    {knowledgePoint.relatedQuestions.map((q: any) => (
                      <div
                        key={q.id}
                        className="p-4 border border-gray-200 rounded-xl hover:border-blue-200 transition-colors"
                      >
                        <h4 className="font-medium text-gray-900 mb-2">{q.title}</h4>
                        <div className="flex items-center gap-3 text-sm">
                          <span className={`px-2 py-0.5 rounded-full ${
                            q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                            q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {q.difficulty === 'easy' ? '简单' : q.difficulty === 'medium' ? '中等' : '困难'}
                          </span>
                          <span className="text-gray-500">{q.points}分</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AcademicCapIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">暂无相关题目</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="text-center py-8">
                <PencilSquareIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">还没有添加笔记</p>
                <button
                  onClick={() => setShowNoteModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  添加第一条笔记
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 相关知识点 */}
        {relatedPoints.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">相关知识点</h3>
            <div className="flex flex-wrap gap-2">
              {relatedPoints.map(point => (
                <Link
                  key={point.id}
                  to={`/student/knowledge/${point.id}`}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-blue-100 hover:text-blue-700 transition-colors"
                >
                  {point.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 添加笔记弹窗 */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">添加笔记</h3>
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="写下你的笔记..."
              className="w-full h-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowNoteModal(false)}
                className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={addNote}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                保存笔记
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeDetail;
